import React from "react";
import SquareButton from "@themuse/design-system/lib/components/Buttons/SquareButton";
import TextLink from "@themuse/design-system/lib/components/Text/TextLink";
import { TrackingObject } from "@themuse/design-system/lib/types/TrackingTypes";

import styles from "./JobQuizContainer.module.scss";

interface JobQuizProcessingProps {
    processingTitle: string;
    reviewTitle: string;
    reviewRetakeText: string;
    reviewViewResultsText: string;
    delay?: number;
    showProcessing?: boolean;
    clickTrackingData?: TrackingObject;
    onViewResults: () => void;
    onReset: () => void;
}

interface JobQuizReviewProps {
    reviewTitle: string;
    reviewRetakeText: string;
    reviewViewResultsText: string;
    showReview?: boolean;
    clickTrackingData?: TrackingObject;
    onResult: () => void;
    onReset: () => void;
}

/**
 * Renders a JobQuizProcessing component for displaying processing state during quiz completion.
 * Handles loading state, displays processing title, loading images, and triggers result view.
 */
const JobQuizProcessing = (props: JobQuizProcessingProps) => {
    const {
        delay,
        processingTitle,
        reviewTitle,
        reviewRetakeText,
        reviewViewResultsText,
        showProcessing,
        clickTrackingData,
        onViewResults,
        onReset
    } = props;
    const [loading, setLoading] = React.useState(true);

    const Image1 = "https://assets.themuse.com/images/job-quiz-image-01.webp";
    const Image2 = "https://assets.themuse.com/images/job-quiz-image-02.webp";
    const Image3 = "https://assets.themuse.com/images/job-quiz-image-03.webp";
    const LoadingGif = "https://assets.themuse.com/images/job-quiz-loading.webp";
    const inViewClass = showProcessing ? styles.viewer : "";

    React.useEffect(() => {
        if (showProcessing) {
            const timeout = delay ?? 2000;
            setTimeout(() => {
                setLoading(false);
                onViewResults?.();
            }, timeout);
        } else {
            setLoading(true);
        }
    }, [delay, onViewResults, showProcessing]);

    return loading ? (
        <div className={[styles.contentBody, inViewClass].join(" ")}>
            <p className={`${styles.title} ${styles.center}`}>{processingTitle}</p>
            <div className={styles.loadingBody}>
                <img
                    className={styles.loadingGif}
                    src={LoadingGif}
                    alt="loading..."
                    height="105"
                    width="105"
                    loading="lazy"
                />

                <img
                    className={styles.imageBubble_1}
                    src={Image1}
                    alt="Woman interviewing"
                    height="75"
                    width="75"
                    loading="lazy"
                />
                <img
                    className={styles.imageBubble_2}
                    src={Image2}
                    alt="Woman laughing"
                    height="100"
                    width="100"
                    loading="lazy"
                />
                <img
                    className={styles.imageBubble_3}
                    src={Image3}
                    alt="Man and woman smiling"
                    height="100"
                    width="100"
                    loading="lazy"
                />
            </div>
        </div>
    ) : (
        <JobQuizReview
            reviewTitle={reviewTitle}
            reviewRetakeText={reviewRetakeText}
            reviewViewResultsText={reviewViewResultsText}
            showReview={!loading}
            onResult={onViewResults}
            onReset={onReset}
            clickTrackingData={clickTrackingData}
        />
    );
};
JobQuizProcessing.displayName = "JobQuizProcessing";

/**
 * Renders a review section of a job quiz.
 */
const JobQuizReview = (props: JobQuizReviewProps) => {
    const {
        reviewTitle,
        reviewRetakeText,
        reviewViewResultsText,
        showReview,
        clickTrackingData,
        onResult = () => { },
        onReset = () => { }
    } = props;

    const extraClass = showReview ? styles.viewer : "";

    return (
        <div className={[styles.contentBody, extraClass].join(" ")}>
            <p className={`${styles.title} ${styles.center}`}>{reviewTitle}</p>
            <div className={styles.loadingBody}>
                <SquareButton
                    className={styles.primaryCta}
                    onClick={onResult}
                    data-tracking-props={JSON.stringify({
                        ...clickTrackingData,
                        sp_click_button_text: reviewViewResultsText,
                        sp_click_target_page_section: "search"
                    })}
                    data-tracking-event-type="click"
                >
                    {reviewViewResultsText}
                </SquareButton>
                <TextLink
                    className={styles.secondaryCta}
                    onClick={onReset}
                    data-tracking-props={JSON.stringify({
                        ...clickTrackingData,
                        sp_click_button_text: reviewRetakeText
                    })}
                    data-tracking-event-type="click"
                >
                    {reviewRetakeText}
                </TextLink>
            </div>
        </div>
    );
};
JobQuizReview.displayName = "JobQuizReview";

export {
    JobQuizProcessing,
    JobQuizReview
};
export type {
    JobQuizProcessingProps,
    JobQuizReviewProps
};
